import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';
import printPdf from 'utils/printPdf';
import { uniq } from 'lodash';
import { Helmet } from 'react-helmet';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import DocumentPreviewFillForm from 'components/common/DocumentPreviewFillForm';
import TemplatePreviewFillForm from 'components/settings/TemplatePreviewFillForm';

import { fetchPatient } from 'redux/modules/patients';
import { fetchTemplates } from 'redux/modules/templates';
import { createPatientDocument } from 'redux/modules/patientsDocuments';
import { fetchBusiness } from 'redux/modules/business';
import { addNotification } from 'redux/modules/notifications';
import { fetchSystemSettings } from 'redux/modules/systemSettings';
import { fetchUser } from 'redux/modules/users';

import documentToPDF from 'utils/documentToPDF';
import templateToPDF from 'utils/templateToPDF';
import documents from 'config/documents';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class PatientPrint extends Component {
  constructor(props) {
    super(props);

    this.state = {
      navigationSections: [],
      documentImages: null,
    };

    this.onTemplatesRequest = this.onTemplatesRequest.bind(this);
    this.onSignatureChange = this.onSignatureChange.bind(this);
    this.onNavigationUpdate = this.onNavigationUpdate.bind(this);
    this.importDependencies = this.importDependencies.bind(this);
  }

  componentDidMount() {
    const { actions, match, account, location, history } = this.props;
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ isMounted: true });
    this.importDependencies();
    if (window.historyState && !location.state) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: window.historyState,
      });
    }
    actions.fetchPatient({
      'Record Number': match.params.id,
      triggerLoading: true,
    });
    actions.fetchBusiness({
      triggerLoading: true,
    });
    actions.fetchSystemSettings({
      triggerLoading: true,
    });
    this.onTemplatesRequest();
    actions.fetchUser({
      ID: account.ID,
      triggerLoading: true,
    });
  }

  componentWillReceiveProps(nextProps) {
    const {
      patient,
      loading,
      history,
      location,
      variant,
    } = this.props;
    if (loading > 0
      && nextProps.loading === 0
      && ((variant === 'document' && !nextProps.document)
        || (variant === 'template' && !nextProps.template))) {
      if (nextProps.patient) {
        history.replace(`/charts/${patient['Record Number']}`);
      } else {
        history.replace('/charts');
      }
    }
    if (nextProps.patient) {
      this.onNavigationUpdate({
        patient: nextProps.patient,
        templates: nextProps.templates,
        signature: location.state && location.state.signature
          ? location.state.signature : null,
      });
    }
  }

  componentWillUnmount() {
    this.setState({ isMounted: false });
  }

  onTemplatesRequest() {
    const { actions, location, match: { params: { template } } } = this.props;
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    let selectedTemplates = Object.keys(params)
      .filter(item => item.indexOf('template') !== -1)
      .map(item => Number(item.replace('template-', '')));
    selectedTemplates = template
      ? uniq([Number(template), ...selectedTemplates]) : selectedTemplates;
    actions.fetchTemplates({
      triggerLoading: true,
      filters: [{
        name: 'ID',
        operation: 'in',
        val: selectedTemplates,
      }],
    });
  }

  onSignatureChange(signature) {
    this.onNavigationUpdate({
      patient: this.props.patient,
      templates: this.props.templates,
      signature,
    });
  }

  onNavigationUpdate({
    patient,
    templates,
    signature,
  }) {
    const { location } = this.props;
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    const navigationSections = Object.keys(params).reduce((accumulator, currentValue) => {
      if (currentValue.indexOf('document') !== -1) {
        const currentDocument = documents
          .find(item => item.ID === Number(currentValue.replace('document-', '')));
        switch (currentDocument.Type) {
          case 'General':
            return [
              {
                title: 'General',
                items: [
                  ...accumulator[0] && accumulator[0].items ? accumulator[0].items : [],
                  {
                    title: currentDocument.Name,
                    id: `Document_${currentDocument.ID}`,
                    href: `/charts/${patient['Record Number']}/print/document/${currentDocument.ID}`,
                    ...signature ? {
                      state: { signature },
                    } : {},
                  },
                ],
              },
              ...accumulator.slice(1),
            ];
          case 'CMS':
            return [
              ...accumulator.slice(0, 1),
              {
                title: 'CMS',
                items: [
                  ...accumulator[1] && accumulator[1].items ? accumulator[1].items : [],
                  {
                    title: currentDocument.Name,
                    id: `Document_${currentDocument.ID}`,
                    href: `/charts/${patient['Record Number']}/print/document/${currentDocument.ID}`,
                    ...signature ? {
                      state: { signature },
                    } : {},
                  },
                ],
              },
              ...accumulator.slice(2),
            ];
          case 'Delivery Ticket':
            return [
              ...accumulator.slice(0, 2),
              {
                title: 'Delivery Tickets',
                items: [
                  ...accumulator[2] && accumulator[2].items ? accumulator[2].items : [],
                  {
                    title: currentDocument.Name,
                    id: `Document_${currentDocument.ID}`,
                    href: `/charts/${patient['Record Number']}/print/document/${currentDocument.ID}`,
                    ...signature ? {
                      state: { signature },
                    } : {},
                  },
                ],
              },
              ...accumulator.slice(3),
            ];
          case 'Form 1500':
            return [
              ...accumulator.slice(0, 3),
              {
                title: '1500 Forms',
                items: [
                  ...accumulator[3] && accumulator[3].items ? accumulator[3].items : [],
                  {
                    title: currentDocument.Name,
                    id: `Document_${currentDocument.ID}`,
                    href: `/charts/${patient['Record Number']}/print/document/${currentDocument.ID}`,
                    ...signature ? {
                      state: { signature },
                    } : {},
                  },
                ],
              },
              ...accumulator.slice(4),
            ];
          case 'Prior Authorization Template':
            return [
              ...accumulator.slice(0, 4),
              {
                title: 'Prior Authorization Templates',
                items: [
                  ...accumulator[4] && accumulator[4].items ? accumulator[4].items : [],
                  {
                    title: currentDocument.Name,
                    id: `Document_${currentDocument.ID}`,
                    href: `/charts/${patient['Record Number']}/print/document/${currentDocument.ID}`,
                    ...signature ? {
                      state: { signature },
                    } : {},
                  },
                ],
              },
              ...accumulator.slice(5),
            ];
          case 'Assessment Form':
            return [
              ...accumulator.slice(0, 5),
              {
                title: 'Assessment Forms',
                items: [
                  ...accumulator[5] && accumulator[5].items ? accumulator[5].items : [],
                  {
                    title: currentDocument.Name,
                    id: `Document_${currentDocument.ID}`,
                    href: `/charts/${patient['Record Number']}/print/document/${currentDocument.ID}`,
                    ...signature ? {
                      state: { signature },
                    } : {},
                  },
                ],
              },
              ...accumulator.slice(6),
            ];
          default:
            return accumulator;
        }
      }
      if (templates.length > 0 && currentValue.indexOf('template') !== -1) {
        const currentTemplate = templates
          .find(item => item.ID === Number(currentValue.replace('template-', '')));
        return [
          ...accumulator.slice(0, 6),
          {
            title: 'Templates',
            items: [
              ...accumulator[6] && accumulator[6].items ? accumulator[6].items : [],
              {
                title: currentTemplate.Name,
                id: `Template_${currentTemplate.ID}`,
                href: `/charts/${patient['Record Number']}/print/template/${currentTemplate.ID}`,
                ...signature ? {
                  state: { signature },
                } : {},
              },
            ],
          },
        ];
      }
      return accumulator;
    }, new Array(7));
    this.setState({ navigationSections });
  }

  async importDependencies() {
    const { document, variant, appInfo } = this.props;
    if (variant === 'document') {
      const documentImages = [];
      const pageImagesPromises = document.Pages.map((page, index) => (
        import(`../../${page.imageUrl}`)
          .then((image) => {
            documentImages[index] = image.default;
          })));
      Promise.all(pageImagesPromises).then(() => {
        if (this.state.isMounted) {
          this.setState({
            documentImages,
          });
        }
      });
    }
  }

  render() {
    const {
      patient,
      loading,
      variant,
      template,
      document,
      business,
      systemSettings,
      actions,
      appInfo,
      account,
      currentUser,
      location,
      printNavigationFormValues,
      selectedFormValues,
      templates,
      record,
    } = this.props;
    const { navigationSections, documentImages } = this.state;
    const documentWithImages = document && documentImages ? {
      ...document,
      Pages: document.Pages.map((page, index) => ({
        ...page,
        image: documentImages[index],
      })),
    } : null;
    let initialValues = {};
    let signatureFieldNames = [];
    if (variant === 'document' && patient && business.ID && Object.keys(systemSettings).length > 0) {
      const fields = document.Pages.reduce((accumulator, currentValue) => ([
        ...accumulator,
        ...currentValue.fields ? currentValue.fields : [],
      ]), []);
      signatureFieldNames = fields
        .filter(item => item.type === 'signature' && item.getValue)
        .map(item => item.name);
      initialValues = fields.reduce((accumulator, currentValue) => ({
        ...accumulator,
        ...{ [currentValue.name]:
          currentValue.getValue ? currentValue.getValue({
            patient,
            business,
            systemSettings,
            account: {
              ...account,
              ...currentUser ? { ...currentUser } : {},
            },
            ...location.state && location.state.signature ? {
              signature: location.state.signature,
            } : {},
          }) : null },
      }), {
        Name: document.Name,
        'Show Charge': true,
        'Show Background Template': true,
      });
    }
    const onlyPrintHistoryRecord = record.filter(item => item.pathname.indexOf('print') === -1).length === 0;
    return (
      <Layout
        variant="print"
        navigationSections={loading === 0 ? navigationSections.filter(item => item) : null}
        {...patient ? {
          titleText: `${patient['Last Name']}, ${patient['First Name']}`,
        } : {}}
      >
        {(loading > 0 || !patient) ? (
          <Progress />
        ) : (
          <Fragment>
            {(variant === 'document' && document && documentImages) &&
              <Fragment>
                <Helmet>
                  <title>
                    {appTitle} {titleSeparator} {patient['First Name']} {
                      patient['Last Name']} {titleSeparator} {document.Name}
                  </title>
                </Helmet>
                <DocumentPreviewFillForm
                  form={`${patient['Record Number']}-patient-${document.ID}-documentPreviewFill`}
                  variant={onlyPrintHistoryRecord ? '_blank' : '_self'}
                  document={documentWithImages}
                  initialValues={initialValues}
                  signatureFieldNames={signatureFieldNames}
                  onSignatureChange={this.onSignatureChange}
                  business={business}
                  appInfo={appInfo}
                  patient={patient}
                  onSubmitCustom={printOnSubmitSuccess =>
                    (values) => {
                      const printNavigationKeys = printNavigationFormValues
                        ? Object.keys(printNavigationFormValues).filter(item => printNavigationFormValues[item] === true)
                        : [];
                      if (printNavigationKeys.length > 0) {
                        const printDocumentName = [];
                        return printNavigationKeys
                          .sort((a, b) => {
                            const documentA = documents.find(item => item.ID === Number(a.split('_')[1]));
                            const documentB = documents.find(item => item.ID === Number(b.split('_')[1]));
                            return (documentA.BatchPrintPosition || 100) - (documentB.BatchPrintPosition || 100);
                          })
                          .reduce((accumulator, currentValue) => {
                            if (currentValue.indexOf('Document') !== -1) {
                              const documentID = Number(currentValue.split('_')[1]);
                              const selectedDocument = documents
                                .find(item => item.ID === documentID);
                              const currentFormName = `${patient['Record Number']}-patient-${documentID}-documentPreviewFill`;
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedDocument.Name);
                              return accumulator.then(prevPDF => documentToPDF({
                                inputPDF: prevPDF,
                                document: selectedDocument,
                                patient,
                                business,
                                systemSettings,
                                appInfo,
                                ...location.state && location.state.signature ? {
                                  signature: location.state.signature,
                                } : {},
                                account: {
                                  ...account,
                                  ...currentUser ? { ...currentUser } : {},
                                },
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                            if (currentValue.indexOf('Template') !== -1) {
                              const templateID = Number(currentValue.split('_')[1]);
                              const selectedTemplate = templates
                                .find(item => item.ID === templateID);
                              const currentFormName = `${patient['Record Number']}-patient-${templateID}-templatePreviewFill`;
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedTemplate.Name);
                              return accumulator.then(prevPDF => templateToPDF({
                                inputPDF: prevPDF,
                                template: selectedTemplate,
                                patient,
                                business,
                                ...location.state && location.state.signature ? {
                                  signature: location.state.signature,
                                } : {},
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                            return accumulator;
                          }, Promise.resolve()).then(PDF => ({
                            blob: PDF.output('blob'),
                            Name: uniq(printDocumentName).join('-'),
                            printOnSubmitSuccess,
                          }));
                      }
                      return documentToPDF({
                        document: documentWithImages,
                        patient,
                        business,
                        systemSettings,
                        appInfo,
                        values,
                        ...location.state && location.state.signature ? {
                          signature: location.state.signature,
                        } : {},
                        account: {
                          ...account,
                          ...currentUser ? { ...currentUser } : {},
                        },
                      }).then(PDF => ({
                        blob: PDF.output('blob'),
                        Name: values.Name,
                        printOnSubmitSuccess,
                      }));
                    }}
                  onSubmitSuccess={(values) => {
                    if (values.printOnSubmitSuccess) {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
                      } else {
                        printPdf(values.blob);
                      }
                    } else {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        const fileBlob = values.blob;
                        fileBlob.lastModifiedDate = new Date();
                        actions.createPatientDocument({
                          values: {
                            'Record Number': patient['Record Number'],
                            Document: fileBlob,
                            Name: `${values.Name}.pdf`,
                          },
                        });
                      } else {
                        const file = new File(
                          [values.blob],
                          `${values.Name}.pdf`,
                          { type: 'application/pdf', lastModified: Date.now() },
                        );
                        actions.createPatientDocument({
                          values: {
                            'Record Number': patient['Record Number'],
                            Document: file,
                          },
                        });
                      }
                      actions.addNotification({
                        message: `"${values.Name}.pdf" was saved to Documents in ${patient['First Name']} ${patient['Last Name']} chart`,
                        variant: 'success',
                      });
                    }
                  }}
                />
              </Fragment>
            }
            {(variant === 'template' && template) &&
              <Fragment>
                <Helmet>
                  <title>
                    {appTitle} {titleSeparator} {patient['First Name']} {
                      patient['Last Name']} {titleSeparator} {template.Name}
                  </title>
                </Helmet>
                <TemplatePreviewFillForm
                  form={`${patient['Record Number']}-patient-${template.ID}-templatePreviewFill`}
                  variant={onlyPrintHistoryRecord ? '_blank' : '_self'}
                  template={template}
                  patient={patient}
                  business={business}
                  onSignatureChange={this.onSignatureChange}
                  initialValues={{
                    Name: template.Name,
                    ...location.state && location.state.signature
                      ? { Questions: template.Questions.map((currentValue) => {
                        if (currentValue['Question Type'] === 'Signature') {
                          return location.state.signature;
                        }
                        return null;
                      }) } : {},
                  }}
                  onSubmitCustom={printOnSubmitSuccess =>
                    (values) => {
                      const printNavigationKeys = printNavigationFormValues
                        ? Object.keys(printNavigationFormValues).filter(item => printNavigationFormValues[item] === true)
                        : [];
                      if (printNavigationKeys.length > 0) {
                        const printDocumentName = [];
                        return printNavigationKeys
                          .sort((a, b) => {
                            const documentA = documents.find(item => item.ID === Number(a.split('_')[1]));
                            const documentB = documents.find(item => item.ID === Number(b.split('_')[1]));
                            return (documentA.BatchPrintPosition || 100) - (documentB.BatchPrintPosition || 100);
                          })
                          .reduce((accumulator, currentValue) => {
                            if (currentValue.indexOf('Document') !== -1) {
                              const documentID = Number(currentValue.split('_')[1]);
                              const selectedDocument = documents
                                .find(item => item.ID === documentID);
                              const currentFormName = `${patient['Record Number']}-patient-${documentID}-documentPreviewFill`;
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedDocument.Name);
                              return accumulator.then(prevPDF => documentToPDF({
                                inputPDF: prevPDF,
                                document: selectedDocument,
                                patient,
                                business,
                                systemSettings,
                                appInfo,
                                ...location.state && location.state.signature ? {
                                  signature: location.state.signature,
                                } : {},
                                account: {
                                  ...account,
                                  ...currentUser ? { ...currentUser } : {},
                                },
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                            if (currentValue.indexOf('Template') !== -1) {
                              const templateID = Number(currentValue.split('_')[1]);
                              const selectedTemplate = templates
                                .find(item => item.ID === templateID);
                              const currentFormName = `${patient['Record Number']}-patient-${templateID}-templatePreviewFill`;
                              const currentFormValues = selectedFormValues[currentFormName];
                              printDocumentName.push(selectedTemplate.Name);
                              return accumulator.then(prevPDF => templateToPDF({
                                inputPDF: prevPDF,
                                template: selectedTemplate,
                                patient,
                                business,
                                ...location.state && location.state.signature ? {
                                  signature: location.state.signature,
                                } : {},
                                ...currentFormValues ? { values: currentFormValues } : {},
                              }));
                            }
                            return accumulator;
                          }, Promise.resolve()).then(PDF => ({
                            blob: PDF.output('blob'),
                            Name: uniq(printDocumentName).join('-'),
                            printOnSubmitSuccess,
                          }));
                      }
                      return templateToPDF({
                        template,
                        patient,
                        business,
                        ...location.state && location.state.signature ? {
                          signature: location.state.signature,
                        } : {},
                        values,
                      }).then(PDF => ({
                        blob: PDF.output('blob'),
                        Name: values.Name,
                        printOnSubmitSuccess,
                      }));
                    }}
                  onSubmitSuccess={(values) => {
                    if (values.printOnSubmitSuccess) {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
                      } else {
                        printPdf(values.blob);
                      }
                    } else {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        const fileBlob = values.blob;
                        fileBlob.lastModifiedDate = new Date();
                        actions.createPatientDocument({
                          values: {
                            'Record Number': patient['Record Number'],
                            Document: fileBlob,
                            Name: `${values.Name}.pdf`,
                          },
                        });
                      } else {
                        const file = new File(
                          [values.blob],
                          `${values.Name}.pdf`,
                          { type: 'application/pdf', lastModified: Date.now() },
                        );
                        actions.createPatientDocument({
                          values: {
                            'Record Number': patient['Record Number'],
                            Document: file,
                          },
                        });
                      }
                      actions.addNotification({
                        message: `"${values.Name}.pdf" was saved to Documents in ${patient['First Name']} ${patient['Last Name']} chart`,
                        variant: 'success',
                      });
                    }
                  }}
                />
              </Fragment>
            }
          </Fragment>
        )}
      </Layout>
    );
  }
}

PatientPrint.propTypes = {
  variant: PropTypes.oneOf(['document', 'template']).isRequired,
  patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
  }),
  document: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  templates: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  template: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object.isRequired,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchPatient: PropTypes.func.isRequired,
    fetchTemplates: PropTypes.func.isRequired,
    createPatientDocument: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
    fetchSystemSettings: PropTypes.func.isRequired,
    fetchUser: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      document: PropTypes.string,
      template: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    state: PropTypes.object,
  }).isRequired,
  appInfo: PropTypes.shape({
    'APP NAME': PropTypes.string,
  }).isRequired,
};

PatientPrint.defaultProps = {
  patient: null,
  document: null,
  template: null,
  business: {},
};

const mapStateToProps = (store, ownProps) => {
  const printNavigationFormValues = getFormValues('printNavigationForm')(store);
  const patient = store.patients.data.length > 0
    ? store.patients.data.find(patient => patient['Record Number'] === Number(ownProps.match.params.id))
    : null;
  const printNavigationKeys = printNavigationFormValues
    ? Object.keys(printNavigationFormValues) : [];
  let selectedFormValues = {};
  if (patient && printNavigationKeys.length > 0) {
    selectedFormValues = printNavigationKeys
      .filter(item => printNavigationFormValues[item] === true)
      .reduce((accumulator, currentValue) => {
        if (currentValue.indexOf('Document') !== -1) {
          const documentID = Number(currentValue.split('_')[1]);
          const currentFormName = `${patient['Record Number']}-patient-${documentID}-documentPreviewFill`;
          const currentFormValues = getFormValues(currentFormName)(store);
          return {
            ...accumulator,
            ...currentFormValues ? { [currentFormName]: currentFormValues } : {},
          };
        }
        if (currentValue.indexOf('Template') !== -1) {
          const templateID = Number(currentValue.split('_')[1]);
          const currentFormName = `${patient['Record Number']}-patient-${templateID}-templatePreviewFill`;
          const currentFormValues = getFormValues(currentFormName)(store);
          return {
            ...accumulator,
            ...currentFormValues ? { [currentFormName]: currentFormValues } : {},
          };
        }
        return accumulator;
      }, {});
  }
  return {
    patient,
    document: documents.find(item => item.ID === Number(ownProps.match.params.document)),
    templates: store.templates.data,
    template: store.templates.data.find(item => item.ID === Number(ownProps.match.params.template)),
    business: store.business.data,
    systemSettings: store.systemSettings.data,
    appInfo: store.appInfo,
    account: store.account,
    currentUser: store.users.data.find(item => item.ID === store.account.ID),
    loading: store.patients.loading
      + store.templates.loading
      + store.business.loading
      + store.systemSettings.loading
      + store.users.loading,
    printNavigationFormValues,
    selectedFormValues,
    record: store.routerParams.record,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatient,
    fetchTemplates,
    createPatientDocument,
    fetchBusiness,
    addNotification,
    fetchSystemSettings,
    fetchUser,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientPrint);



// WEBPACK FOOTER //
// ./src/pages/charts/PatientPrint.js